import { Drawer, InputAdornment, TextField } from '@material-ui/core';
import { debounce, isEmpty } from 'lodash-es';
import React, { useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';
import NumberFormat from 'react-number-format';

import { track } from '~/src/dash/analytics/analytics';
import { IPublisher } from '~/src/dash/api/console-api';
import { createPlatformConnection } from '~/src/dash/apiV2/platform-connect/platform-connect';
import { FocusedTextfield } from '~/src/dash/components/focused-textfield';
import { InfoTooltipIcon } from '~/src/dash/components/icons/InfoIcon';
import { IconText } from '~/src/dash/components/IconText';
import { IListViewTableDataCell } from '~/src/dash/components/tables/ListViewTable/common';
import { TextWithBackground } from '~/src/dash/components/TextWithBackground';
import {
  borderRadius,
  ButtonV2,
  Col,
  Cols,
  Div,
  Row,
  Rows,
  spacing,
  Text,
  TextButton,
} from '~/src/dash/publicaUI';
import { publicaColors } from '~/src/dash/utils/colors';
import { IS_AUTHORIZED } from '~/src/dash/utils/permission_service';
import { useBaseStore } from '~/src/dash/views/base/contexts';
import {
  EPlatformConnectOfferType,
  IPlatformConnectionWithNamesAndType,
  IPlatformConnectUiParams,
  renderExchangeAdornment,
  RowLineBreak,
} from '~/src/dash/views/platform-connect/common';

interface ICartDrawerProps {
  isModalOpen: boolean;
  setIsModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  publisher: IPublisher;
  activeRowInfo: Record<string, IListViewTableDataCell>;
  onFetchUniqueDemandOpportunitiesList: (params: IPlatformConnectUiParams) => void;
  error: boolean;
  setError: React.Dispatch<React.SetStateAction<any>>;
  setSnackbarMessage: React.Dispatch<React.SetStateAction<string>>;
}

export const UniqueDemandDrawer = ({
  isModalOpen,
  setIsModalOpen,
  publisher,
  activeRowInfo,
  onFetchUniqueDemandOpportunitiesList,
  error,
  setError,
  setSnackbarMessage,
}: ICartDrawerProps) => {
  const { user, currencySymbol, currencyMultiplier, isSuperUser } = useBaseStore();
  const [proposedCpmValue, setProposedCpmValue] = useState('0');
  const [currNote, setCurrNote] = useState('');
  const [isActionPending, setIsActionPending] = useState(false);

  const onCloseDrawer = () => {
    unstable_batchedUpdates(() => {
      setIsModalOpen(false);
      setProposedCpmValue('0');
      setError(false);
      setSnackbarMessage('');
      setCurrNote('');
      setIsActionPending(false);
    });
  };

  const onChangeProposedCpmValue = (value: string) => {
    setProposedCpmValue(value);
  };

  const onClickCreatePlatformConnection = () => {
    setIsActionPending(true);
    const demandSourcesString = activeRowInfo?.CustomLeftColumn?.value;
    const data = {
      DemandSource: demandSourcesString,
      BuyerPublisherID: parseInt(publisher.ID, 10),
      SellerPublisherID: parseInt(activeRowInfo.ID.value, 10),
      ActionPublisherID: parseInt(publisher.ID, 10),
      Type: String(EPlatformConnectOfferType.PLATFORM_CONNECTION_UNIQUE_DEMAND),
      BidFloor: parseInt(proposedCpmValue, 10),
    } as Partial<IPlatformConnectionWithNamesAndType>;
    createPlatformConnection(publisher.ID, data, currNote)
      .then(() => {
        track('PC - Sends offer', { is_super_user: isSuperUser }, user.user);
        onFetchUniqueDemandOpportunitiesList &&
          onFetchUniqueDemandOpportunitiesList({ currPage: 0, rowsPerPage: 25 });
      })
      .then(() => {
        onCloseDrawer();
        unstable_batchedUpdates(() => {
          setError(false);
          setSnackbarMessage('Successfully sent platform connection offer.');
        });
      })
      .catch(() => {
        unstable_batchedUpdates(() => {
          setIsActionPending(false);
          setError(true);
          if (proposedCpmValue && parseInt(proposedCpmValue, 10) > 0) {
            setSnackbarMessage('Error creating platform connection.');
          } else {
            setSnackbarMessage('Please enter a Proposed CPM.');
          }
        });
      });
  };

  return (
    <>
      <Drawer anchor='right' open={isModalOpen} onClose={onCloseDrawer}>
        <Rows gap={spacing.medium} style={{ width: '40vw', overflow: 'auto', maxHeight: '100vh' }}>
          <Row paddingBottom={spacing.none}>
            <Rows gap={spacing.medium}>
              <Row noPadding>
                <Cols
                  style={{
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Col>
                    <TextWithBackground color='purple' text='New Offer' />
                  </Col>
                  <Col>
                    <TextButton
                      color={publicaColors.darkGray}
                      style={{
                        display: 'flex',
                        borderRadius: borderRadius.small,
                        padding: '0px',
                        justifyContent: 'center',
                        alignItems: 'center',
                        minWidth: '80px',
                        minHeight: '32px',
                      }}
                      aria-label='Cancel'
                      onClick={onCloseDrawer}
                    >
                      <IconText
                        bold
                        color={publicaColors.darkGray}
                        icon='close'
                        text='Close'
                        extraSmall
                        gap={spacing.extraSmall}
                      />
                    </TextButton>
                  </Col>
                </Cols>
              </Row>
              <Row noVerticalPadding paddingLeft={spacing.small}>
                <Text large heavy color={publicaColors.darkGray}>
                  {!isEmpty(activeRowInfo) ? activeRowInfo.Name?.value : 'Anonymous Seller'}
                </Text>
              </Row>
            </Rows>
          </Row>
          <RowLineBreak />
          <Row noVerticalPadding>
            <Rows gap={spacing.medium}>
              <Row noPadding>
                <Rows>
                  <Row noPadding>
                    <IconText
                      small
                      bold
                      color={publicaColors.darkGray}
                      icon='globe'
                      text='Demand Sources'
                    />
                  </Row>
                  <Row noVerticalPadding paddingLeft={spacing.large}>
                    <Text small color={publicaColors.gray}>
                      {activeRowInfo?.CustomLeftColumn?.value}
                    </Text>
                  </Row>
                </Rows>
              </Row>
              <Row noPadding>
                <Rows>
                  <Row noPadding>
                    <Cols
                      style={{
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <Cols>
                        <Col>
                          <IconText
                            small
                            bold
                            color={publicaColors.darkGray}
                            icon='shopping'
                            iconColor={publicaColors.black}
                            text='Proposed CPM'
                          />
                        </Col>
                        <Col>
                          <InfoTooltipIcon
                            tooltipText='Offer CPM will be used as Bid Floor'
                            tooltipPlacement='top'
                          />
                        </Col>
                      </Cols>
                      {/* Creating New Offer */}
                      <Col displayFlex style={{ alignItems: 'center' }}>
                        <NumberFormat
                          autoFocus
                          name='Proposed CPM'
                          value={proposedCpmValue === '0' ? '' : proposedCpmValue}
                          disabled={
                            !IS_AUTHORIZED(['write_manage_seller_requests'], user.user).isAuthorized
                          }
                          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                            onChangeProposedCpmValue(event.target.value)
                          }
                          error={error}
                          customInput={TextField}
                          thousandSeparator={true}
                          InputProps={{
                            startAdornment: <InputAdornment position='start'>$</InputAdornment>,
                            endAdornment: renderExchangeAdornment(
                              String(proposedCpmValue || 0),
                              currencySymbol,
                              currencyMultiplier,
                            ),
                          }}
                          style={{ maxWidth: '152px' }}
                        />
                      </Col>
                    </Cols>
                  </Row>
                </Rows>
              </Row>
              <Row noPadding>
                <Rows>
                  <Row noPadding>
                    <Cols
                      style={{
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <Col>
                        <IconText
                          small
                          bold
                          color={publicaColors.darkGray}
                          gap='6px'
                          icon='timer'
                          iconColor={publicaColors.black}
                          text='Acceptance window'
                          style={{
                            padding: '4px 6px',
                          }}
                        />
                      </Col>
                      <Col>
                        <Text small bold color={publicaColors.black}>
                          4 weeks
                        </Text>
                      </Col>
                    </Cols>
                  </Row>
                  <Row noVerticalPadding paddingLeft={spacing.large}>
                    <Text small bold color={publicaColors.darkGray}>
                      Time-limit for your offer
                    </Text>
                  </Row>
                </Rows>
              </Row>
            </Rows>
          </Row>
          <RowLineBreak />
          <Row noVerticalPadding>
            <Rows>
              <Row noPadding>
                <Col>
                  <IconText
                    small
                    bold
                    color={publicaColors.darkGray}
                    icon='message'
                    iconColor={publicaColors.veryDarkGray}
                    text='Notes'
                  />
                </Col>
              </Row>
              <Row noVerticalPadding paddingLeft={spacing.large}>
                <FocusedTextfield
                  fullWidth
                  multiline
                  minRows={3}
                  placeholder='Add a note to the seller about this opportunity...'
                  label=''
                  name='Notes'
                  value={currNote}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCurrNote(e.target.value)}
                />
              </Row>
            </Rows>
          </Row>
          <RowLineBreak />
          {/* Make Offer Button */}
          <Row largeHorizontalPadding noVerticalPadding>
            <Rows
              gap={spacing.medium}
              style={{
                alignItems: 'center',
              }}
            >
              <Row noPadding>
                <ButtonV2
                  style={{
                    borderRadius: borderRadius.small,
                    padding: spacing.small,
                  }}
                  aria-label='Make Offer'
                  onClick={debounce(onClickCreatePlatformConnection, 100)}
                  disabled={
                    !IS_AUTHORIZED(['write_manage_seller_requests'], user.user).isAuthorized ||
                    isActionPending
                  }
                >
                  <IconText
                    color={publicaColors.white}
                    icon='paper-plane'
                    text='Make Offer'
                    bold
                    extraSmall
                    gap={spacing.extraSmall}
                    noPadding
                  />
                </ButtonV2>
              </Row>
              <Row noPadding>
                <Div displayFlex>
                  <Text extraSmall color={publicaColors.darkGray}>
                    The seller is given the opportunity to accept these terms or make you a counter
                    offer on CPM. Once the seller accepts, you'll have the final go before delivery
                    starts.
                  </Text>
                </Div>
              </Row>
            </Rows>
          </Row>
        </Rows>
      </Drawer>
    </>
  );
};
